import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  getLeaderboard,
  type LeaderboardEntry,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { EASE_OUT } from '../../../shared/motion.ts'
import { LoadNotice } from '../components/LoadNotice'

type Period = 'daily' | 'weekly' | 'alltime'

export function Leaderboard() {
  const { t } = useTranslation()
  const { token } = useAuth()
  const [period, setPeriod] = useState<Period>('daily')
  const [rows, setRows] = useState<LeaderboardEntry[]>([])
  const [rowsPeriod, setRowsPeriod] = useState<Period | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    void (async () => {
      try {
        const data = await getLeaderboard(period, token ?? undefined)
        if (cancelled) return
        setRows(data)
        setRowsPeriod(period)
      } catch {
        if (cancelled) return
        setError(t('leaderboard.loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [period, retry, token, t])

  const visibleRows = rowsPeriod === period ? rows : []

  const tabs: { id: Period; label: string }[] = [
    { id: 'daily', label: t('leaderboard.daily') },
    { id: 'weekly', label: t('leaderboard.weekly') },
    { id: 'alltime', label: t('leaderboard.alltime') },
  ]

  return (
    <div className="page">
      <h2 className="section-title">{t('leaderboard.title')}</h2>
      <div className="tabs" role="tablist" aria-label={t('leaderboard.title')}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={period === tab.id}
            className={period === tab.id ? 'active' : ''}
            onClick={() => setPeriod(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="lb-skeleton" aria-hidden>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="lb-skel-row" />
          ))}
        </div>
      )}
      {error ? (
        <LoadNotice
          message={error}
          onRetry={() => setRetry((value) => value + 1)}
          busy={loading}
        />
      ) : null}
      {!loading && !error && visibleRows.length === 0 && (
        <div className="card muted">{t('leaderboard.empty')}</div>
      )}
      {!loading && visibleRows.length > 0 && (
        <table className="lb-table">
          <thead>
            <tr>
              <th scope="col">{t('leaderboard.rank')}</th>
              <th scope="col">{t('leaderboard.player')}</th>
              <th scope="col">{t('leaderboard.score')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => (
              <motion.tr
                key={`${r.rank}-${r.username}`}
                className={r.rank <= 3 ? `lb-top lb-top-${r.rank}` : undefined}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: Math.min(i * 0.03, 0.3),
                  duration: 0.2,
                  ease: EASE_OUT,
                }}
              >
                <td>
                  <span className="lb-rank">{r.rank}</span>
                </td>
                <td>{r.username}</td>
                <td>{r.score}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
